import React from 'react';
import { BusinessIdeaCard, TaskCard, ParticleBackground } from '../components';
import { useBusinessIdea } from '../hooks/useBusinessIdea';
import { useNextTasks } from '../hooks/useNextTask';
import { useNavigate } from 'react-router-dom';

const HomePage: React.FC = () => {
  const { businessIdea, loading, error } = useBusinessIdea();
  const { nextTasks, loading: nextTasksLoading } = useNextTasks(6); // Show up to 6 next tasks
  const navigate = useNavigate();

  const handleTaskClick = (taskId: string) => {
    navigate(`/task/${taskId}`);
  };

  return (
    <div className="min-h-screen bg-background-dark">
      <ParticleBackground star />
      <main className="p-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-bold font-heading text-white mb-6">My Business</h1>

          {/* Business Idea Card */}
          {loading && (
            <div className="mb-8">
              <div className="bg-background-surface/95 backdrop-blur-sm rounded-lg border border-gray-600 p-6">
                <div className="animate-pulse">
                  <div className="h-6 bg-gray-700 rounded w-3/4 mb-4"></div>
                  <div className="h-4 bg-gray-700 rounded w-full mb-2"></div>
                  <div className="h-4 bg-gray-700 rounded w-5/6 mb-4"></div>
                  <div className="h-16 bg-gray-700 rounded w-full"></div>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-8 bg-red-900/20 backdrop-blur-sm border border-red-500 rounded-lg p-6">
              <p className="text-red-400">Failed to load your business idea: {error}</p>
            </div>
          )}

          {businessIdea && !loading && !error && (
            <div className="mb-8">
              <BusinessIdeaCard businessIdea={businessIdea} />
            </div>
          )}

          {/* Next Tasks Section */}
          {businessIdea && !loading && !error && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold font-heading text-white">What's Next?</h2>
                <button
                  onClick={() => navigate('/completed-tasks')}
                  className="text-text-secondary hover:text-white transition-colors text-sm flex items-center gap-1"
                >
                  <span>View Completed</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>

              <div className="bg-background-surface/95 backdrop-blur-sm rounded-lg border border-gray-600 p-6">
                {nextTasksLoading && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[...Array(6)].map((_, index) => (
                      <div
                        key={index}
                        className="bg-background-surface/95 backdrop-blur-sm rounded-lg border border-gray-600 p-4"
                      >
                        <div className="animate-pulse">
                          <div className="flex gap-2 mb-3">
                            <div className="h-5 bg-gray-700 rounded w-16"></div>
                            <div className="h-5 bg-gray-700 rounded w-20"></div>
                          </div>
                          <div className="h-5 bg-gray-700 rounded w-3/4 mb-2"></div>
                          <div className="h-4 bg-gray-700 rounded w-full mb-2"></div>
                          <div className="h-4 bg-gray-700 rounded w-5/6 mb-4"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {nextTasks.length > 0 && !nextTasksLoading && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {nextTasks.map((task) => (
                      <div key={task.taskId} onClick={() => handleTaskClick(task.taskId)}>
                        <TaskCard task={task} />
                      </div>
                    ))}
                  </div>
                )}

                {nextTasks.length === 0 && !nextTasksLoading && (
                  <div className="text-center py-8">
                    <div className="text-6xl mb-4">🎉</div>
                    <h3 className="text-xl font-semibold text-white mb-2">Congratulations!</h3>
                    <p className="text-text-secondary">
                      You've completed all the tasks in your startup journey. Your business is ready
                      to launch!
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default HomePage;
